import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { breachApi, processingApi, serviceProviderApi, organizationApi, openPdfExport } from '../services/api';
import {
  DataBreach,
  BreachStatus,
  BreachSeverity,
  BreachCategory,
  Organization,
  ProcessingActivity,
  ProcessingStatus,
  ServiceProvider
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  TabContainer,
  Tab,
  MessageBox,
  ObjectStatus,
  Icon,
  BusyIndicator,
  MessageStrip,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Dialog,
  Bar,
  TextArea,
  Label
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import QuickCreateDialog from '../components/QuickCreateDialog';

const statusConfig: Record<BreachStatus, { label: string; state: string }> = {
  [BreachStatus.DETECTED]: { label: 'Entdeckt', state: 'Warning' },
  [BreachStatus.TRIAGE]: { label: 'Triage', state: 'Warning' },
  [BreachStatus.INVESTIGATION]: { label: 'Untersuchung', state: 'Information' },
  [BreachStatus.AUTHORITY_NOTIFIED]: { label: 'Behörde benachrichtigt', state: 'Information' },
  [BreachStatus.SUBJECTS_NOTIFIED]: { label: 'Betroffene benachrichtigt', state: 'Information' },
  [BreachStatus.REMEDIATION]: { label: 'Gegenmaßnahmen', state: 'Information' },
  [BreachStatus.CLOSED]: { label: 'Abgeschlossen', state: 'Success' }
};

const severityConfig: Record<BreachSeverity, { label: string; state: string }> = {
  [BreachSeverity.LOW]: { label: 'Gering', state: 'Success' },
  [BreachSeverity.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [BreachSeverity.HIGH]: { label: 'Hoch', state: 'Error' },
  [BreachSeverity.CRITICAL]: { label: 'Kritisch', state: 'Error' }
};

const categoryConfig: Record<BreachCategory, { label: string }> = {
  [BreachCategory.UNAUTHORIZED_ACCESS]: { label: 'Unbefugter Zugriff' },
  [BreachCategory.DATA_LOSS]: { label: 'Datenverlust' },
  [BreachCategory.UNAUTHORIZED_DISCLOSURE]: { label: 'Unbefugte Weitergabe' },
  [BreachCategory.ACCIDENTAL_DELETION]: { label: 'Versehentliche Löschung' },
  [BreachCategory.DEVICE_LOSS]: { label: 'Geräteverlust' },
  [BreachCategory.RANSOMWARE]: { label: 'Ransomware' },
  [BreachCategory.MISCONFIGURATION]: { label: 'Fehlkonfiguration' },
  [BreachCategory.SOCIAL_ENGINEERING]: { label: 'Social Engineering' },
  [BreachCategory.OTHER]: { label: 'Sonstiges' }
};

const processingStatusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

// Valid next statuses for each status
const validTransitions: Record<BreachStatus, BreachStatus[]> = {
  [BreachStatus.DETECTED]: [BreachStatus.TRIAGE, BreachStatus.INVESTIGATION],
  [BreachStatus.TRIAGE]: [BreachStatus.INVESTIGATION, BreachStatus.CLOSED],
  [BreachStatus.INVESTIGATION]: [BreachStatus.AUTHORITY_NOTIFIED, BreachStatus.REMEDIATION, BreachStatus.CLOSED],
  [BreachStatus.AUTHORITY_NOTIFIED]: [BreachStatus.SUBJECTS_NOTIFIED, BreachStatus.REMEDIATION, BreachStatus.CLOSED],
  [BreachStatus.SUBJECTS_NOTIFIED]: [BreachStatus.REMEDIATION, BreachStatus.CLOSED],
  [BreachStatus.REMEDIATION]: [BreachStatus.CLOSED],
  [BreachStatus.CLOSED]: []
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', padding: '0.5rem 0', borderBottom: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
    <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', width: '180px', flexShrink: 0 }}>{label}</Text>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

export default function BreachDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEdit, canDelete } = useUserRole();

  const [breach, setBreach] = useState<DataBreach | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [linkedActivities, setLinkedActivities] = useState<ProcessingActivity[]>([]);
  const [allActivities, setAllActivities] = useState<ProcessingActivity[]>([]);
  const [linkedProviders, setLinkedProviders] = useState<ServiceProvider[]>([]);
  const [allProviders, setAllProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLinkActivityDialog, setShowLinkActivityDialog] = useState(false);
  const [showLinkProviderDialog, setShowLinkProviderDialog] = useState(false);
  const [quickCreateType, setQuickCreateType] = useState<string | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [measuresDialogOpen, setMeasuresDialogOpen] = useState(false);
  const [measuresForm, setMeasuresForm] = useState({
    immediate_measures: '',
    remediation_plan: '',
    lessons_learned: ''
  });

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const orgsResponse = await organizationApi.getAll();
    if (orgsResponse.success && orgsResponse.data && orgsResponse.data.length > 0) {
      const org = orgsResponse.data[0];
      setCurrentOrg(org);
      if (id) {
        const [breachRes, linkedActRes, allActRes, linkedProvRes, allProvRes] = await Promise.all([
          breachApi.getById(parseInt(id), org.id),
          breachApi.getLinkedActivities(parseInt(id), org.id),
          processingApi.getByOrganization(org.id),
          breachApi.getLinkedProviders(parseInt(id), org.id),
          serviceProviderApi.getByOrganization(org.id)
        ]);
        if (breachRes.success && breachRes.data) {
          setBreach(breachRes.data);
        } else {
          navigate('/breaches');
        }
        if (linkedActRes.success && linkedActRes.data) setLinkedActivities(linkedActRes.data);
        if (allActRes.success && allActRes.data) setAllActivities(allActRes.data);
        if (linkedProvRes.success && linkedProvRes.data) setLinkedProviders(linkedProvRes.data);
        if (allProvRes.success && allProvRes.data) setAllProviders(allProvRes.data);
      }
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!breach || !currentOrg) return;
    const response = await breachApi.delete(breach.id, currentOrg.id);
    if (response.success) {
      navigate('/breaches');
    }
  };

  const handleLinkActivity = async (activityId: number) => {
    if (!breach || !currentOrg) return;
    await breachApi.linkProcessing(breach.id, activityId, currentOrg.id);
    loadData();
    setShowLinkActivityDialog(false);
  };

  const handleUnlinkActivity = async (activityId: number) => {
    if (!breach || !currentOrg) return;
    await breachApi.unlinkProcessing(breach.id, activityId, currentOrg.id);
    loadData();
  };

  const handleLinkProvider = async (providerId: number) => {
    if (!breach || !currentOrg) return;
    await breachApi.linkProvider(breach.id, providerId, currentOrg.id);
    loadData();
    setShowLinkProviderDialog(false);
  };

  const handleUnlinkProvider = async (providerId: number) => {
    if (!breach || !currentOrg) return;
    await breachApi.unlinkProvider(breach.id, providerId, currentOrg.id);
    loadData();
  };

  const openMeasuresDialog = () => {
    if (!breach) return;
    setMeasuresForm({
      immediate_measures: breach.immediate_measures || '',
      remediation_plan: breach.remediation_plan || '',
      lessons_learned: breach.lessons_learned || ''
    });
    setMeasuresDialogOpen(true);
  };

  const handleSaveMeasures = async () => {
    if (!breach || !currentOrg) return;
    const response = await breachApi.update(breach.id, currentOrg.id, {
      immediate_measures: measuresForm.immediate_measures,
      remediation_plan: measuresForm.remediation_plan,
      lessons_learned: measuresForm.lessons_learned
    });
    if (response.success && response.data) {
      setBreach(response.data);
      setMeasuresDialogOpen(false);
    }
  };

  const handleStatusChange = async (newStatus: BreachStatus) => {
    if (!breach || !currentOrg) return;
    const response = await breachApi.update(breach.id, currentOrg.id, { status: newStatus });
    if (response.success && response.data) {
      setBreach(response.data);
    }
  };

  if (loading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  if (!breach) return null;

  const linkedActIds = new Set(linkedActivities.map(a => a.id));
  const unlinkableActivities = allActivities.filter(a => !linkedActIds.has(a.id));
  const linkedProvIds = new Set(linkedProviders.map(p => p.id));
  const unlinkableProviders = allProviders.filter(p => !linkedProvIds.has(p.id));

  // 72h deadline
  const deadlineDate = new Date(breach.authority_deadline);
  const now = new Date();
  const diffMs = deadlineDate.getTime() - now.getTime();
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
  const isTerminal = breach.status === BreachStatus.CLOSED;
  const isOverdue = diffHours < 0 && !isTerminal;
  const authorityNotYetNotified = breach.status !== BreachStatus.AUTHORITY_NOTIFIED &&
    breach.status !== BreachStatus.SUBJECTS_NOTIFIED &&
    breach.status !== BreachStatus.REMEDIATION &&
    breach.status !== BreachStatus.CLOSED;
  const showOverdueBanner = isOverdue && authorityNotYetNotified;

  const deadlineLabel = isTerminal
    ? deadlineDate.toLocaleDateString('de-DE')
    : diffHours < 0
      ? 'ÜBERFÄLLIG'
      : `noch ${diffHours}h`;

  const nextStatuses = validTransitions[breach.status] || [];
  const linkCount = linkedActivities.length + linkedProviders.length;

  return (
    <div className="page-layout--detail">
      {/* Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Start" style={{ marginBottom: '1.5rem' }}>
        <div>
          <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.25rem' }}>
            <Button icon="nav-back" design="Transparent" onClick={() => navigate('/breaches')} tooltip="Zurück zur Liste" />
            <Title level="H3">{breach.title}</Title>
            <ObjectStatus state={severityConfig[breach.severity]?.state as any} showDefaultIcon>
              {severityConfig[breach.severity]?.label}
            </ObjectStatus>
            <ObjectStatus state={statusConfig[breach.status]?.state as any} showDefaultIcon>
              {statusConfig[breach.status]?.label}
            </ObjectStatus>
          </FlexBox>
          <Text style={{ color: 'var(--sapNeutralTextColor)', marginLeft: '2.75rem' }}>
            {categoryConfig[breach.category]?.label} — 72h-Frist:{' '}
            <span style={{ color: isOverdue ? 'var(--sapNegativeColor)' : undefined, fontWeight: isOverdue ? '700' : undefined }}>
              {deadlineLabel}
            </span>
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('breaches/' + id, currentOrg.id)}>PDF</Button>
          {canEdit && (
            <Button icon="edit" design="Default" onClick={() => navigate(`/breaches/${id}/edit`)}>
              Bearbeiten
            </Button>
          )}
          {canDelete && (
            <Button icon="delete" design="Negative" onClick={() => setShowDeleteDialog(true)}>
              Löschen
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      {/* Overdue Warning Banner */}
      {showOverdueBanner && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          Die 72-Stunden-Frist zur Meldung an die Aufsichtsbehörde ist überschritten! Der Vorfall hätte bis zum {deadlineDate.toLocaleString('de-DE')} gemeldet werden müssen ({Math.abs(diffHours)} Stunden überfällig).
        </MessageStrip>
      )}

      {/* Tabs */}
      <TabContainer onTabSelect={() => {}} style={{ marginBottom: '1.5rem' }}>
        <Tab text="Überblick" icon="hint" selected>
          <div style={{ padding: '1rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              {/* Incident Info */}
              <Card style={{ flex: '1 1 500px' }} header={<CardHeader titleText="Vorfall-Informationen" avatar={<Icon name="alert" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Titel"><Text>{breach.title}</Text></InfoRow>
                  <InfoRow label="Kategorie"><Text>{categoryConfig[breach.category]?.label}</Text></InfoRow>
                  <InfoRow label="Schweregrad">
                    <ObjectStatus state={severityConfig[breach.severity]?.state as any} showDefaultIcon>
                      {severityConfig[breach.severity]?.label}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Vorfall am"><Text>{new Date(breach.incident_at).toLocaleDateString('de-DE')}</Text></InfoRow>
                  <InfoRow label="Entdeckt am"><Text>{new Date(breach.detected_at).toLocaleDateString('de-DE')}</Text></InfoRow>
                  <InfoRow label="72h-Frist">
                    <Text style={{ color: isOverdue ? 'var(--sapNegativeColor)' : undefined, fontWeight: isOverdue ? '600' : undefined }}>
                      {deadlineDate.toLocaleString('de-DE')}
                      {!isTerminal && (isOverdue ? ` (${Math.abs(diffHours)}h überfällig)` : ` (noch ${diffHours}h)`)}
                    </Text>
                  </InfoRow>
                  <InfoRow label="Status">
                    <ObjectStatus state={statusConfig[breach.status]?.state as any} showDefaultIcon>
                      {statusConfig[breach.status]?.label}
                    </ObjectStatus>
                  </InfoRow>

                  {/* Status Change Actions */}
                  {canEdit && nextStatuses.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <Text style={{ fontWeight: '600', fontSize: '0.8125rem', display: 'block', marginBottom: '0.5rem' }}>Status ändern:</Text>
                      <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                        {nextStatuses.map(s => (
                          <Button
                            key={s}
                            design="Default"
                            onClick={() => handleStatusChange(s)}
                            style={{ fontSize: '0.8125rem' }}
                          >
                            {statusConfig[s]?.label}
                          </Button>
                        ))}
                      </FlexBox>
                    </div>
                  )}
                </div>
              </Card>

              {/* Impact Info */}
              <Card style={{ flex: '1 1 300px' }} header={<CardHeader titleText="Auswirkungen" avatar={<Icon name="warning" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Betroffene Daten"><Text>{breach.affected_data || '-'}</Text></InfoRow>
                  <InfoRow label="Anzahl Betroffene"><Text>{breach.affected_count != null ? breach.affected_count.toLocaleString('de-DE') : '-'}</Text></InfoRow>
                  <InfoRow label="Ursache"><Text>{breach.cause || '-'}</Text></InfoRow>
                  <InfoRow label="Mögliche Folgen"><Text>{breach.consequences || '-'}</Text></InfoRow>
                </div>
              </Card>
            </FlexBox>
          </div>
        </Tab>

        <Tab text="Risiko & Meldung" icon="quality-issue">
          <div style={{ padding: '1rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              <Card style={{ flex: '1 1 100%' }} header={<CardHeader titleText="Risikobewertung & Meldepflichten" avatar={<Icon name="quality-issue" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Risikobewertung"><Text>{breach.risk_assessment || '-'}</Text></InfoRow>
                  <InfoRow label="Meldung an Behörde">
                    <ObjectStatus state={breach.authority_required ? 'Error' : 'Success'} showDefaultIcon>
                      {breach.authority_required ? 'Erforderlich' : 'Nicht erforderlich'}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Benachrichtigung Betroffene">
                    <ObjectStatus state={breach.subjects_required ? 'Error' : 'Success'} showDefaultIcon>
                      {breach.subjects_required ? 'Erforderlich' : 'Nicht erforderlich'}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Meldetext Behörde"><Text>{breach.authority_report || '-'}</Text></InfoRow>
                  <InfoRow label="Benachrichtigungstext"><Text>{breach.subjects_report || '-'}</Text></InfoRow>
                  <InfoRow label="Behörde benachrichtigt am">
                    <Text>{breach.authority_notified_at ? new Date(breach.authority_notified_at).toLocaleString('de-DE') : '-'}</Text>
                  </InfoRow>
                  <InfoRow label="Betroffene benachrichtigt am">
                    <Text>{breach.subjects_notified_at ? new Date(breach.subjects_notified_at).toLocaleString('de-DE') : '-'}</Text>
                  </InfoRow>
                </div>
              </Card>
            </FlexBox>
          </div>
        </Tab>

        <Tab text="Maßnahmen" icon="shield">
          <div style={{ padding: '1rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              <Card
                style={{ flex: '1 1 100%' }}
                header={
                  <CardHeader
                    titleText="Maßnahmen & Lessons Learned"
                    avatar={<Icon name="shield" />}
                    action={
                      canEdit ? (
                        <Button icon="edit" design="Transparent" onClick={openMeasuresDialog}>
                          Bearbeiten
                        </Button>
                      ) : undefined
                    }
                  />
                }
              >
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Sofortmaßnahmen"><Text style={{ whiteSpace: 'pre-wrap' }}>{breach.immediate_measures || '-'}</Text></InfoRow>
                  <InfoRow label="Abhilfeplan"><Text style={{ whiteSpace: 'pre-wrap' }}>{breach.remediation_plan || '-'}</Text></InfoRow>
                  <InfoRow label="Lessons Learned"><Text style={{ whiteSpace: 'pre-wrap' }}>{breach.lessons_learned || '-'}</Text></InfoRow>
                </div>
              </Card>
            </FlexBox>
          </div>
        </Tab>

        <Tab text={`Verknüpfungen (${linkCount})`} icon="chain-link">
          <div style={{ padding: '1rem 0' }}>
            {/* Linked Processing Activities */}
            <Card
              style={{ marginBottom: '1rem' }}
              header={
                <CardHeader
                  titleText={`${linkedActivities.length} verknüpfte Verarbeitungen`}
                  avatar={<Icon name="documents" />}
                  action={
                    <FlexBox style={{ gap: '0.5rem' }}>
                      <Button icon="add-document" design="Transparent" onClick={() => { setQuickCreateType('processing'); setQuickCreateOpen(true); }}>
                        Neu erstellen
                      </Button>
                      <Button icon="add" design="Transparent" onClick={() => setShowLinkActivityDialog(true)}>
                        Verknüpfen
                      </Button>
                    </FlexBox>
                  }
                />
              }
            >
              {linkedActivities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Icon name="documents" style={{ fontSize: '2rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '0.5rem' }} />
                  <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
                    Noch keine Verarbeitungen verknüpft.
                  </Text>
                  <div style={{ marginTop: '0.75rem' }}>
                    <Button icon="add" design="Default" onClick={() => setShowLinkActivityDialog(true)}>
                      Verarbeitung verknüpfen
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '0 1rem 1rem' }}>
                  <Table
                    style={{ width: '100%' }}
                    columns={
                      <>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Verarbeitung</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Aktion</Text></TableColumn>
                      </>
                    }
                  >
                    {linkedActivities.map(activity => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          <div>
                            <Text
                              style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--sapLinkColor)' }}
                              onClick={() => navigate(`/processing/${activity.id}`)}
                            >
                              {activity.name}
                            </Text>
                            <Text style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                              {activity.department}
                            </Text>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ObjectStatus state={processingStatusConfig[activity.status]?.state as any} showDefaultIcon>
                            {processingStatusConfig[activity.status]?.label}
                          </ObjectStatus>
                        </TableCell>
                        <TableCell>
                          <Button
                            icon="disconnect"
                            design="Transparent"
                            onClick={() => handleUnlinkActivity(activity.id)}
                            tooltip="Verknüpfung entfernen"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>
                </div>
              )}
            </Card>

            {/* Linked Service Providers */}
            <Card
              header={
                <CardHeader
                  titleText={`${linkedProviders.length} verknüpfte Dienstleister`}
                  avatar={<Icon name="supplier" />}
                  action={
                    <FlexBox style={{ gap: '0.5rem' }}>
                      <Button icon="add-document" design="Transparent" onClick={() => { setQuickCreateType('service-provider'); setQuickCreateOpen(true); }}>
                        Neu erstellen
                      </Button>
                      <Button icon="add" design="Transparent" onClick={() => setShowLinkProviderDialog(true)}>
                        Verknüpfen
                      </Button>
                    </FlexBox>
                  }
                />
              }
            >
              {linkedProviders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Icon name="supplier" style={{ fontSize: '2rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '0.5rem' }} />
                  <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
                    Noch keine Dienstleister verknüpft.
                  </Text>
                  <div style={{ marginTop: '0.75rem' }}>
                    <Button icon="add" design="Default" onClick={() => setShowLinkProviderDialog(true)}>
                      Dienstleister verknüpfen
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '0 1rem 1rem' }}>
                  <Table
                    style={{ width: '100%' }}
                    columns={
                      <>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Dienstleister</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Rolle</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Aktion</Text></TableColumn>
                      </>
                    }
                  >
                    {linkedProviders.map(provider => (
                      <TableRow key={provider.id}>
                        <TableCell>
                          <div>
                            <Text
                              style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--sapLinkColor)' }}
                              onClick={() => navigate(`/service-providers/${provider.id}`)}
                            >
                              {provider.name}
                            </Text>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Text>{provider.role}</Text>
                        </TableCell>
                        <TableCell>
                          <Button
                            icon="disconnect"
                            design="Transparent"
                            onClick={() => handleUnlinkProvider(provider.id)}
                            tooltip="Verknüpfung entfernen"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>
                </div>
              )}
            </Card>
          </div>
        </Tab>
      </TabContainer>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <MessageBox
          type="Confirm"
          open={showDeleteDialog}
          onClose={(e: any) => {
            if (e.detail.action === 'OK') handleDelete();
            setShowDeleteDialog(false);
          }}
          titleText="Vorfall löschen"
        >
          Möchten Sie den Vorfall „{breach.title}" wirklich löschen? Alle Verknüpfungen werden ebenfalls entfernt.
        </MessageBox>
      )}

      {/* Link Activity Dialog */}
      {showLinkActivityDialog && (
        <MessageBox
          type="Confirm"
          open={showLinkActivityDialog}
          onClose={() => setShowLinkActivityDialog(false)}
          titleText="Verarbeitung verknüpfen"
        >
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {unlinkableActivities.length === 0 ? (
              <Text>Alle Verarbeitungen sind bereits verknüpft.</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {unlinkableActivities.map(activity => (
                  <Button
                    key={activity.id}
                    design="Default"
                    onClick={() => handleLinkActivity(activity.id)}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {activity.name} ({activity.department})
                  </Button>
                ))}
              </div>
            )}
          </div>
        </MessageBox>
      )}

      {/* Link Provider Dialog */}
      {showLinkProviderDialog && (
        <MessageBox
          type="Confirm"
          open={showLinkProviderDialog}
          onClose={() => setShowLinkProviderDialog(false)}
          titleText="Dienstleister verknüpfen"
        >
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {unlinkableProviders.length === 0 ? (
              <Text>Alle Dienstleister sind bereits verknüpft.</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {unlinkableProviders.map(provider => (
                  <Button
                    key={provider.id}
                    design="Default"
                    onClick={() => handleLinkProvider(provider.id)}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {provider.name}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </MessageBox>
      )}

      {/* Maßnahmen Edit Dialog */}
      <Dialog
        open={measuresDialogOpen}
        headerText="Maßnahmen bearbeiten"
        onAfterClose={() => setMeasuresDialogOpen(false)}
        style={{ width: '600px' }}
        footer={
          <Bar
            endContent={
              <FlexBox>
                <Button design="Transparent" onClick={() => setMeasuresDialogOpen(false)} style={{ marginRight: '0.5rem' }}>
                  Abbrechen
                </Button>
                <Button design="Emphasized" onClick={handleSaveMeasures}>
                  Speichern
                </Button>
              </FlexBox>
            }
          />
        }
      >
        <div style={{ padding: '1rem', minWidth: '500px' }}>
          <div style={{ marginBottom: '1rem' }}>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Sofortmaßnahmen</Label>
            <TextArea
              value={measuresForm.immediate_measures}
              onInput={(e: any) => setMeasuresForm(prev => ({ ...prev, immediate_measures: e.target.value }))}
              rows={4}
              style={{ width: '100%' }}
              placeholder="Welche Sofortmaßnahmen wurden ergriffen, um den Vorfall einzudämmen?"
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Abhilfeplan</Label>
            <TextArea
              value={measuresForm.remediation_plan}
              onInput={(e: any) => setMeasuresForm(prev => ({ ...prev, remediation_plan: e.target.value }))}
              rows={4}
              style={{ width: '100%' }}
              placeholder="Wie wird das zugrundeliegende Problem langfristig behoben?"
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Lessons Learned</Label>
            <TextArea
              value={measuresForm.lessons_learned}
              onInput={(e: any) => setMeasuresForm(prev => ({ ...prev, lessons_learned: e.target.value }))}
              rows={4}
              style={{ width: '100%' }}
              placeholder="Welche Erkenntnisse nehmen Sie aus dem Vorfall mit?"
            />
          </div>
        </div>
      </Dialog>

      {quickCreateType && currentOrg && (
        <QuickCreateDialog
          type={quickCreateType as 'processing' | 'service-provider' | 'tom' | 'dsr' | 'breach'}
          organizationId={currentOrg.id}
          open={quickCreateOpen}
          onClose={() => setQuickCreateOpen(false)}
          onCreated={() => {
            setQuickCreateOpen(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}
